import { ChevronDown, FileText, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useDiaryMeals } from "@/hooks/useDiaryMeals";
import { localDateOf } from "@/lib/body-measurements";
import { addDays, DAY_KEYS } from "@/lib/days";
import { type DiaryMeal, mealsByTime, mealTypeLabel } from "@/lib/diary";
import { deleteDiaryMeal, saveDiaryMeal } from "@/lib/repos/diary";
import type { MealDiaryConfig, MealDiaryDayData } from "@/lib/schema";
import { countPhrase } from "@/widgets/describe";
import { FutureDayCard, isFutureDate } from "@/widgets/FutureDayCard";
import type { WidgetDayViewProps } from "@/widgets/types";
import { useAutoCollapse } from "@/widgets/useAutoCollapse";
import { MealDiaryModal } from "./MealDiaryModal";
import { scoreDay } from "./scoring";

type Props = WidgetDayViewProps<MealDiaryConfig>;

export function DayView(props: Props) {
  // Diary meals are date-keyed and global — the viewed day maps to a date via
  // the week's startDate; the settings preview falls back to today.
  const date = props.week
    ? addDays(props.week.startDate, DAY_KEYS.indexOf(props.dayKey))
    : localDateOf(new Date());

  // Keyed by date so an open form never carries over to another day when
  // navigating within a mounted week.
  if (!props.readOnly && isFutureDate(date)) {
    return (
      <FutureDayCard
        configId={props.config.id}
        label={props.config.label}
        kindClass="meal-diary"
      />
    );
  }

  return <DateView key={date} date={date} {...props} />;
}

function DateView({
  config,
  data,
  date,
  dayKey,
  onChangeDay,
  readOnly = false,
}: Props & { date: string }) {
  const { meals: stored, loading } = useDiaryMeals(date);
  const [modal, setModal] = useState<{
    meal: DiaryMeal | null;
    autoEstimate?: boolean;
  } | null>(null);

  const day = (data ?? {}) as MealDiaryDayData;
  const finished = day.finished === true;
  const meals = mealsByTime(stored);
  const score = scoreDay(config, data);

  const save = async (meal: DiaryMeal) => {
    await saveDiaryMeal(date, meal);
    setModal(null);
  };

  const remove = async (mealId: string) => {
    if (readOnly) return;
    const remaining = await deleteDiaryMeal(date, mealId);
    // A finished day with nothing eaten claims too much, so the last meal
    // takes the marker with it. Any other change leaves it standing: a
    // forgotten snack added later does not undo the day.
    if (remaining.length === 0 && finished) {
      onChangeDay({ ...day, finished: false } satisfies MealDiaryDayData);
    }
  };

  const toggleFinished = () => {
    if (readOnly) return;
    if (!finished && meals.length === 0) return;
    onChangeDay({ ...day, finished: !finished } satisfies MealDiaryDayData);
  };

  // Ferdiglogget (CONTEXT.md): the day is Måltider ferdig.
  const loggingCompleted = !readOnly && finished;
  const { open, toggle } = useAutoCollapse(dayKey, loggingCompleted, !loading);
  const collapsible = loggingCompleted;
  const collapsed = collapsible && !open;

  return (
    <section
      className={`widget-card meal-diary${readOnly ? " read-only" : ""}${collapsed ? " collapsed" : ""}${loggingCompleted ? " logging-completed" : ""}`}
      data-testid={`widget-day-${config.id}`}
      data-logging-completed={loggingCompleted ? "true" : "false"}
    >
      <header className="widget-card-head">
        <h3 className="widget-card-title">{config.label}</h3>
        <div className="widget-card-head-right">
          <span
            className="widget-card-score"
            data-testid={`widget-score-${config.id}`}
          >
            <b>{score}</b>
            <span className="max"> / {config.points}p</span>
          </span>
          {collapsible ? (
            <button
              type="button"
              className="widget-card-toggle"
              onClick={toggle}
              aria-label={collapsed ? "Utvid" : "Skjul"}
              aria-expanded={!collapsed}
              data-testid={`widget-collapse-${config.id}`}
            >
              <ChevronDown className="chev" size={16} />
            </button>
          ) : null}
        </div>
      </header>
      {collapsed ? (
        <div
          className="widget-card-summary"
          data-testid={`meal-diary-summary-${config.id}`}
        >
          <span className="summary-line">
            {[
              countPhrase(meals.length, "måltid", "måltider"),
              meals.map((meal) => mealTypeLabel(meal.type)).join(", "),
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
      ) : null}
      <div className="widget-card-body">
        {meals.length === 0 ? (
          <p className="hint" data-testid={`meal-diary-empty-${config.id}`}>
            Ingen måltider logget.
          </p>
        ) : (
          <ul className="meal-diary-meals">
            {meals.map((meal) => (
              <li
                key={meal.id}
                className="meal-diary-row"
                data-testid={`meal-diary-row-${meal.id}`}
              >
                <span className="meal-diary-when">
                  <span className="meal-diary-time">{meal.time}</span>
                  <span className="meal-diary-type">
                    {mealTypeLabel(meal.type)}
                  </span>
                </span>
                <span className="meal-diary-text">
                  {meal.ingredients ? (
                    <>
                      <span className="meal-diary-name">{meal.name}</span>
                      {meal.description ? (
                        <span className="meal-diary-description">
                          {meal.description}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <span className="meal-diary-name">
                        {meal.description}
                      </span>
                      <span
                        className="meal-diary-no-ingredients"
                        data-testid={`meal-diary-no-ingredients-${meal.id}`}
                      >
                        Ingen ingrediensliste
                        {!readOnly ? (
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() =>
                              setModal({ meal, autoEstimate: true })
                            }
                            data-testid={`meal-diary-fetch-ingredients-button-${meal.id}`}
                          >
                            Hent ingredienser
                          </button>
                        ) : null}
                      </span>
                    </>
                  )}
                </span>
                {!readOnly ? (
                  <span className="meal-diary-actions">
                    <button
                      type="button"
                      className="icon-btn-edit"
                      onClick={() => setModal({ meal })}
                      aria-label="Rediger måltid"
                      data-testid={`meal-diary-edit-button-${meal.id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      className="icon-btn-delete"
                      onClick={() => void remove(meal.id)}
                      aria-label="Slett måltid"
                      data-testid={`meal-diary-delete-button-${meal.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {!readOnly ? (
          <div className="meal-diary-footer">
            <button
              type="button"
              className="btn ghost icon-btn"
              onClick={() => setModal({ meal: null })}
              data-testid={`meal-diary-add-button-${config.id}`}
            >
              <Plus size={14} />
              Legg til måltid
            </button>
            <Link
              to="/rapport"
              className="btn ghost icon-btn"
              data-testid={`report-button-${config.id}`}
            >
              <FileText size={14} />
              Rapport
            </Link>
            {/* Not offered before the first meal: a finished day with
                nothing eaten would claim too much. */}
            {meals.length > 0 || finished ? (
              <button
                type="button"
                className={finished ? "btn ghost" : "btn-primary"}
                onClick={toggleFinished}
                data-testid={`meal-diary-finish-button-${config.id}`}
              >
                {finished ? "Åpne dagen igjen" : "Ferdig for i dag"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {modal ? (
        <MealDiaryModal
          initial={modal.meal}
          autoEstimate={modal.autoEstimate}
          onSave={(meal) => void save(meal)}
          onClose={() => setModal(null)}
        />
      ) : null}
    </section>
  );
}
